import { formatDate, formatDateWithTimeZone } from "@cp/toolkit";
import {
  type FetchResult,
  type MutationResult,
  type RescheduleSendInboxDraftMutation,
  useRescheduleSendInboxDraftMutation,
} from "@qw/graphql";
import { useToast } from "@qw/ui";
import { useCallback } from "react";

import { useScheduleSendDraftForm } from "./use-schedule-send-draft-form";

type UseRescheduleSendDraftReturn = [
  (id: string, previousScheduledSendAt: Date) => Promise<FetchResult<RescheduleSendInboxDraftMutation> | undefined>,
  MutationResult<RescheduleSendInboxDraftMutation>,
];

export const useRescheduleSendDraft = (): UseRescheduleSendDraftReturn => {
  const { toast } = useToast();
  const { openScheduleSendDraftForm } = useScheduleSendDraftForm();
  const [rescheduleSendDraftMutation, result] = useRescheduleSendInboxDraftMutation();

  const rescheduleSendDraft = useCallback(
    async (id: string, previousScheduledSendAt: Date) => {
      const scheduledSendAt = await openScheduleSendDraftForm({
        defaultValues: {
          scheduledSendAtDate: previousScheduledSendAt,
          scheduledSendAtTime: formatDate(previousScheduledSendAt, "HH:mm"),
        },
      });

      if (!scheduledSendAt) {
        return;
      }

      return rescheduleSendDraftMutation({
        variables: { input: { id, scheduledSendAt } },
        onCompleted: (data) => {
          if (!data?.rescheduleSendInboxDraft) {
            throw new Error("Failed to reschedule draft for sending");
          }
          toast({
            title: "Draft rescheduled",
            description: `Your draft has been rescheduled for sending on ${formatDateWithTimeZone(
              scheduledSendAt,
              "LLL d, yyyy 'at' h:mm a"
            )}.`,
          });
        },
        onError: (error) => {
          toast({
            title: "Failed to reschedule draft for sending",
            description: error instanceof Error ? error.message : "An unexpected error occurred",
            variant: "destructive",
          });
        },
        refetchQueries: ["InboxDrafts", "InboxDraft", "InboxDraftsCount", "InboxDraftsFailedScheduledSendsCount"],
      });
    },
    [rescheduleSendDraftMutation, openScheduleSendDraftForm, toast]
  );

  return [rescheduleSendDraft, result];
};
